import { useMemo, useState } from 'react';
import { ContractPhasesMonthColumns } from './ContractPhasesMonthColumns';
import { ContractPhasesDataRows, RenderBarProps } from './ContractPhasesDataRows';
import { Period, PricingModel } from '@vy/types';
import { VyIconType } from '@vy/ui-design-system';
import { validateNotEmptyArray } from '@vy/utils';
import { useContainerWidth } from '../../../../../../hooks/use-container-width';
import { buildTimelineAxis } from './contract-phases-timeline-utils/build-timeline-axis';

export type PeriodsData = {
  title: string;
  period: Period;
  icon?: VyIconType;
  color?: string;
  backgroundColor?: string;
  pricingModel?: PricingModel;
};

type ContractPhasesTimelineContainerProps = {
  BarComponent: React.FC<RenderBarProps>;
  chartData: PeriodsData[];
  contractLength: Period;
  barWidthFull?: boolean;
};

const CHART_ENTRY_HEIGHT = 70;
const CHART_MINIMUM_HEIGHT = 200;

export const ContractPhasesTimelineContainer: React.FC<ContractPhasesTimelineContainerProps> = ({
  BarComponent,
  chartData,
  contractLength,
}) => {
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();

  const dataHeight = chartData.length * CHART_ENTRY_HEIGHT;
  const minHeight = Math.max(CHART_MINIMUM_HEIGHT, dataHeight);
  const [containerHeight] = useState(minHeight + CHART_ENTRY_HEIGHT);

  validateNotEmptyArray(chartData);

  const { ticks, totalMonths, monthsPerTick, domain } = useMemo(
    () => buildTimelineAxis(contractLength),
    [contractLength],
  );

  return (
    <div
      ref={containerRef}
      style={{ height: containerHeight, overflowX: 'auto', overflowY: 'hidden' }}
      className="relative w-full"
    >
      <ContractPhasesMonthColumns
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        ticks={ticks}
        totalMonths={totalMonths}
        monthsPerTick={monthsPerTick}
        renderData={(_, pixelsPerMonth) => (
          <ContractPhasesDataRows
            phasesData={chartData}
            columnAmount={totalMonths}
            columnWidth={pixelsPerMonth}
            fullPeriod={domain}
            barHeight={60}
            barsGap={10}
            renderBar={(barProps) => <BarComponent {...barProps} />}
          />
        )}
      />
    </div>
  );
};
